import HeroBackgroundImage from 'public/images/shipsticks/hero-background-3.jpg'
import PackStep1Image from 'public/images/shipsticks/pack-step1.jpg'
import PackStep2Image from 'public/images/shipsticks/pack-step2.jpg'

import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { IconNames } from 'lib/settings/icons'

export interface PackPageContent {
  hero: NarrativeHeroProps
  newsletterSignup: {
    title: string
    subtitle: string
  }
  steps: PackStep[]
  tableData: { do: string; "don't": string }[]
}

interface PackItem {
  icon?: IconNames
  header: string | React.ReactNode
  body: string | React.ReactNode
  isRequired?: boolean
}

export interface PackStep {
  header: string | React.ReactNode
  video?: PackStepVideoType
  note?: string
  items: PackItem[]
  image?: string
}

export interface PackStepVideoType {
  header?: string | React.ReactNode
  body?: string
  caption?: string
  video: string
}

const hero = {
  backgroundImage:
    typeof HeroBackgroundImage === 'string' ? HeroBackgroundImage : HeroBackgroundImage.src,
  header: 'How to Pack and Label Your Bags',
  subHeader: "Suitcases, boxes, hard cases - getting them ready to ship is easy. Here's how.",
}

const newsletterSignup = {
  title: "Let's talk",
  subtitle:
    'Becoming a Ship&Play partner is simple, fast and completely free. ' +
    "We'll help you get set up, support your team, and provide the tools you " +
    'need to deliver a better experience from the moment guests book their trip',
}

const steps: PackStep[] = [
  {
    header: "What you'll need",
    items: [
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'A soft travel bag, a hard case, or a box',
        isRequired: true,
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Two copies of your shipping label (printed from your account or confirmation email)',
        isRequired: true,
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Clear packing tape',
        isRequired: true,
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>OPTIONAL:</strong> A stiff arm and, for hard cases and soft bags, a shipping
            luggage tag (aka a fly tag) and zip tie available at most carrier stores
          </div>
        ),
        isRequired: false,
      },
    ],
  },
  {
    header: (
      <>
        <strong>Step 1:</strong> <span style={{ fontSize: '0.9em' }}>Prepare your items</span>
      </>
    ),
    image: typeof PackStep1Image === 'string' ? PackStep1Image : PackStep1Image.src,
    items: [
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Cushion anything fragile and tuck soft items around it so nothing shifts.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body:
          'Shipping sporting gear? Weave a towel between golf heads, or strap skis together, ' +
          'to keep them from banging around.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>Note:</strong> For added peace of mind, some travelers like to remove detachable
            parts - adjustable clubheads, ski bindings, pedals - and pack them inside.
          </div>
        ),
      },
    ],
  },
  {
    header: (
      <>
        <strong>Step 2:</strong> <span style={{ fontSize: '0.9em' }}>Pack your bag</span>
      </>
    ),
    image: typeof PackStep2Image === 'string' ? PackStep2Image : PackStep2Image.src,
    items: [
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Zip up your suitcase or place your gear inside a soft bag, hard case, or box.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Place one copy of the shipping label inside.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>For hard cases and soft bags:</strong> Close the case or bag, and pull any
            straps tight.
          </div>
        ),
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>For boxes:</strong> Tape the box&apos;s seams closed using at least 3 strips of
            tape at each end.
          </div>
        ),
      },
    ],
  },
  {
    header: (
      <>
        <strong>Step 3:</strong> <span style={{ fontSize: '0.9em' }}>Attach the label</span>
      </>
    ),
    video: {
      header: <span style={{ fontSize: '0.5em' }}>How to attach the label</span>,
      video: 'https://youtu.be/C5Lq-g8255o?feature=shared',
      caption: 'Video instructions on how to attach the label',
      body: '',
    },
    items: [
      {
        header: <strong>For boxes</strong>,
        body:
          'Tape the second label to the outside of the box. ' +
          'Cover the entire label with clear packing tape, pressing on it to ' +
          'ensure the label is securely attached.',
      },
      {
        header: <strong>For hard cases and soft bags</strong>,
        body: (
          <div>
            A luggage shipping tag (fly tag) works best. Slide the label into the tag making sure
            it&apos;s visible, then secure it to the bag handle with a zip tie.{' '}
            <strong>NOTE:</strong> Though a tag is preferred, you can tape the label as directed for
            boxes onto a hard case or, for soft cases, onto the flat panel at the bottom of the bag.
          </div>
        ),
      },
    ],
  },
]

const tableData = [
  {
    do: 'Do add extra padding, if you like, such as a jacket or even bubble wrap.',
    "don't": "Don't tape your label to canvas as it might fall off in transit.",
  },
  {
    do: 'Do jot down any settings before removing parts, like adjustable clubheads.',
    "don't": "Don't skimp on the packing tape.",
  },
  {
    do: 'Do consider a reusable shipping label holder if you ship your ' + 'bags often.',
    "don't": "Don't forget the small tools you'll need on arrival, like a golf wrench.",
  },
]

const content: PackPageContent = {
  hero,
  newsletterSignup,
  steps,
  tableData,
}

export interface ShipplayPackContentItem {
  title: string
  body?: React.ReactNode
}

export interface ShipplayPackSplitBlockContent {
  imgUrl: string
  imgAlt: string
  heading: string
  items: ShipplayPackContentItem[]
}

export interface ShipplayHubContent {
  hero: {
    imgUrl: string
    title: {
      prefix: React.ReactNode
      emphasis: React.ReactNode
      suffix?: React.ReactNode
    }
    subtitle: React.ReactNode
  }
  luggage: {
    sectionHeader: {
      prefix: React.ReactNode
      emphasis: React.ReactNode
    }
    whatYoullNeed: ShipplayPackSplitBlockContent
    step1: ShipplayPackSplitBlockContent
  }
}

export const shipplayContent: ShipplayHubContent = {
  hero: {
    imgUrl: '/images/shipplay/htp-boxes-1.webp',
    title: {
      prefix: 'How to Pack & Label',
      emphasis: <> Your Luggage</>,
    },
    subtitle: 'It’s easy to get your luggage ready to ship.',
  },
  luggage: {
    sectionHeader: {
      prefix: 'How to Prepare',
      emphasis: <> Suitcases &amp; Duffel Bags</>,
    },
    whatYoullNeed: {
      imgUrl: '/images/shipplay/htp-print.webp',
      imgAlt: 'Printing a prepaid shipping label',
      heading: 'What You’ll Need',
      items: [
        {
          title: 'Two copies of your shipping label',
          body: (
            <div className="snplay:space-y-4 snplay:text-base">
              <p>&mdash; mailed to you or printed from your account.</p>
              <p>
                <strong>Tip: </strong> We&rsquo;ll mail labels to your chosen address. If
                you&rsquo;re booking last-minute or shipping internationally, you may need to print
                your label from your confirmation email or account.
              </p>
            </div>
          ),
        },
        {
          title: 'Luggage shipping tag & zip tie',
          body: (
            <div className="snplay:space-y-4 snplay:text-base">
              <p>&mdash; included with your mailed label.</p>
              <p>
                <strong>Tip: </strong> Luggage shipping tags (or fly tags) and zip ties are
                available at most carrier stores.
              </p>
            </div>
          ),
        },
        {
          title: 'Clear packing tape',
          body: (
            <div className="snplay:space-y-4 snplay:text-base">
              <p>&mdash; if needed.</p>
            </div>
          ),
        },
      ],
    },
    step1: {
      imgUrl: '/images/shipplay/htp-pack.webp',
      imgAlt: 'Packing step in the shipping process',
      heading: 'Step 1: Prepare Your Bags',
      items: [
        { title: 'Pack your belongings, leaving out any prohibited items.' },
        { title: 'Protect fragile or loose items with bubble wrap or padding.' },
        { title: 'Place one copy of your shipping label inside your luggage.' },
        { title: 'Close all zippered or buckled compartments securely.' },
      ],
    },
  },
}

export default content
